'use client';

import { Z_INDEX } from '@/constants';
import { useModalStore } from '@/store/modal.store';
import type {
  DeadlineInfo,
  StickyBottomBannerProps,
  TextPart,
} from '@/types/sticky-bottom-banner.type';
import { useEffect, useState } from 'react';

// 기본 마감일 계산 함수 (기존 로직)
/**
 * 첫 렌더 값. 서버 렌더와 브라우저의 첫 렌더가 같아야 한다 — 남은 시간을 그 자리에서 계산하면
 * 서버 시각과 브라우저 시각의 초가 달라 매번 React 418(hydration 불일치)이 났다.
 * 화면에 붙은 직후 useEffect 가 실제 남은 시간으로 바꾼다.
 */
const PENDING_DEADLINE: DeadlineInfo = {
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
  isExpired: false,
};

const defaultCalculateDeadline = (): DeadlineInfo => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-indexed
  const date = now.getDate();

  // 이번달 말일 계산
  const lastDay = new Date(year, month + 1, 0).getDate();

  // 이번달 15일 23:59:59
  const thisMonth15th = new Date(year, month, 15, 23, 59, 59, 999);
  // 이번달 말일 23:59:59
  const thisMonthLastDay = new Date(year, month, lastDay, 23, 59, 59, 999);

  // 오늘이 15일 이하인 경우 → 이번달 15일이 마감
  // 오늘이 16일 이후인 경우 → 이번달 말일이 마감
  let candidateDeadline: Date;

  if (date <= 15) {
    candidateDeadline = thisMonth15th;
  } else {
    candidateDeadline = thisMonthLastDay;
  }

  // 선택한 마감일이 이미 지났다면, 다음 기간으로 넘어감
  let deadline: Date;

  if (now.getTime() > candidateDeadline.getTime()) {
    // 마감일이 지났으므로 다음 기간으로 설정
    if (date <= 15) {
      // 15일이 지났으므로 말일로
      deadline = thisMonthLastDay;
    } else {
      // 말일이 지났으므로 다음달 15일로
      const nextMonth = month === 11 ? 0 : month + 1;
      const nextMonthYear = month === 11 ? year + 1 : year;
      deadline = new Date(nextMonthYear, nextMonth, 15, 23, 59, 59, 999);
    }
  } else {
    // 아직 마감일 전이므로 선택한 마감일 사용
    deadline = candidateDeadline;
  }

  // 남은 시간 계산
  const diff = deadline.getTime() - now.getTime();
  const isExpired = diff < 0;

  let days = 0,
    hoursRemaining = 0,
    minutesRemaining = 0,
    secondsRemaining = 0;

  if (!isExpired) {
    days = Math.floor(diff / (1000 * 60 * 60 * 24));
    hoursRemaining = Math.floor(
      (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
    );
    minutesRemaining = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    secondsRemaining = Math.floor((diff % (1000 * 60)) / 1000);
  }

  return {
    days,
    hours: hoursRemaining,
    minutes: minutesRemaining,
    seconds: secondsRemaining,
    isExpired,
  };
};

// 텍스트 파트 렌더링 헬퍼 함수
const renderTextParts = (parts: TextPart[] | undefined) => {
  if (!parts || parts.length === 0) return null;

  return (
    <>
      {parts.map((part, index) => {
        if (part.highlight && part.highlightColor) {
          return (
            <span key={index} style={{ color: part.highlightColor }}>
              {part.text}
            </span>
          );
        }
        return <span key={index}>{part.text}</span>;
      })}
    </>
  );
};

// 버튼 클릭 핸들러
const handleButtonClick = (
  action: StickyBottomBannerProps['button']['action'],
  openModal: () => void,
) => {
  switch (action.type) {
    case 'modal':
      // 커스텀 onOpen 함수가 있으면 사용, 없으면 기본 상담하기 팝업
      if (action.onOpen) {
        action.onOpen();
      } else {
        openModal();
      }
      break;
    case 'link':
      if (action.target === '_blank') {
        window.open(action.url, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = action.url;
      }
      break;
    case 'custom':
      action.onClick();
      break;
  }
};

export default function StickyBottomBanner({
  calculateDeadline = defaultCalculateDeadline,
  showCountdown = true,
  webContent,
  mobileContent,
  button,
  backgroundColor = 'bg-black/75',
  zIndex = Z_INDEX.Header,
  mobileHeight = 'h-[94px]',
  desktopHeight = 'desktop:h-[120px]',
}: StickyBottomBannerProps) {
  const { open: openModal } = useModalStore();
  const [deadlineInfo, setDeadlineInfo] =
    useState<DeadlineInfo>(PENDING_DEADLINE);

  useEffect(() => {
    if (!showCountdown) return;

    setDeadlineInfo(calculateDeadline());

    // 1초마다 업데이트
    const interval = setInterval(() => {
      setDeadlineInfo(calculateDeadline());
    }, 1000);

    return () => clearInterval(interval);
  }, [calculateDeadline, showCountdown]);

  // backgroundColor가 Tailwind 클래스명인지 확인 (bg-로 시작하는지)
  const isBgTailwindClass = button.backgroundColor.startsWith('bg-');
  const buttonBgClassName = isBgTailwindClass ? button.backgroundColor : '';

  // textColor가 Tailwind 클래스명인지 확인 (text-로 시작하는지)
  const isTextTailwindClass = button.textColor?.startsWith('text-') ?? false;
  const buttonTextClassName = isTextTailwindClass ? button.textColor : '';

  // className과 style 조합
  const buttonClassName = `${buttonBgClassName} ${buttonTextClassName}`.trim();
  const buttonStyle = {
    ...(isBgTailwindClass ? {} : { backgroundColor: button.backgroundColor }),
    ...(isTextTailwindClass ? {} : { color: button.textColor || 'white' }),
  };

  return (
    <>
      <div
        className="fixed right-0 bottom-0 left-0"
        style={{
          zIndex,
        }}
      >
        {/** 웹 */}
        <div
          className={`desktop:block desktop:web-w desktop:mx-auto desktop:rounded-t-2xl desktop:px-[52px] desktop:py-[30px] ${desktopHeight} hidden rounded-t-xl ${backgroundColor} px-4 py-3 backdrop-blur-md`}
        >
          <div className="desktop:flex desktop:items-center desktop:justify-between flex items-center justify-between">
            {/* 왼쪽: 프로모션 텍스트 */}
            <div className="desktop:gap-3 flex shrink-0 flex-col items-start">
              {webContent.subtitle && (
                <div className="typo-12 desktop:typo-18 font-medium text-white">
                  {renderTextParts(webContent.subtitle)}
                </div>
              )}
              {webContent.title && (
                <div className="typo-14 desktop:typo-26 font-bold text-white">
                  {renderTextParts(webContent.title)}
                </div>
              )}
            </div>

            {/* 중간: 카운트다운 및 버튼 */}
            <div className="typo-16 desktop:typo-32 flex flex-wrap items-center font-bold whitespace-nowrap text-white tabular-nums">
              {showCountdown && (
                <>
                  {deadlineInfo.isExpired ? '0' : deadlineInfo.days}일{' '}
                  {deadlineInfo.isExpired
                    ? '00'
                    : String(deadlineInfo.hours).padStart(2, '0')}
                  시{' '}
                  {deadlineInfo.isExpired
                    ? '00'
                    : String(deadlineInfo.minutes).padStart(2, '0')}
                  분{' '}
                  {deadlineInfo.isExpired
                    ? '00'
                    : String(deadlineInfo.seconds).padStart(2, '0')}
                  초{' '}
                </>
              )}
              {/* 오른쪽: 버튼 */}
              <button
                onClick={() => handleButtonClick(button.action, openModal)}
                className={`desktop:ml-7 desktop:w-[168px] desktop:h-[64px] desktop:rounded-xl desktop:typo-22 typo-14 shrink-0 cursor-pointer rounded-lg px-4 py-2.5 font-extrabold whitespace-nowrap ${buttonClassName}`}
                style={buttonStyle}
              >
                {button.text}
              </button>
            </div>
          </div>
        </div>

        {/** 모바일 */}
        <div
          className={`desktop:hidden mobile-w mx-auto flex flex-wrap items-center justify-center rounded-t-xl ${backgroundColor} px-6 backdrop-blur-md`}
        >
          <div
            className={`flex ${mobileHeight} w-full items-center justify-between`}
          >
            {/* 왼쪽: 프로모션 텍스트 */}
            <div className="flex shrink-0 flex-col items-start gap-3">
              {mobileContent.title && (
                <div className="typo-16 desktop:typo-26 font-bold text-white">
                  {renderTextParts(mobileContent.title)}
                </div>
              )}
              {showCountdown && (
                <div className="typo-20 desktop:typo-32 flex flex-wrap items-center font-bold whitespace-nowrap text-white tabular-nums">
                  {deadlineInfo.isExpired ? '0' : deadlineInfo.days}일{' '}
                  {deadlineInfo.isExpired
                    ? '00'
                    : String(deadlineInfo.hours).padStart(2, '0')}
                  시{' '}
                  {deadlineInfo.isExpired
                    ? '00'
                    : String(deadlineInfo.minutes).padStart(2, '0')}
                  분{' '}
                  {deadlineInfo.isExpired
                    ? '00'
                    : String(deadlineInfo.seconds).padStart(2, '0')}
                  초
                </div>
              )}
              {mobileContent.subtitle && (
                <div className="typo-14 text-white">
                  {renderTextParts(mobileContent.subtitle)}
                </div>
              )}
            </div>

            {/* 오른쪽: 버튼 */}
            <button
              onClick={() => handleButtonClick(button.action, openModal)}
              className={`desktop:rounded-xl typo-18 h-[46px] w-[135px] shrink-0 cursor-pointer rounded-lg font-extrabold whitespace-nowrap ${buttonClassName}`}
              style={buttonStyle}
            >
              {button.text}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
